/**
 * IBAN und Gläubiger-ID prüfen — dieselbe Rechnung wie im Backend
 * (`apps/api/src/sepa/iban.ts`), nur früher: Ein Zahlendreher soll auffallen,
 * während der Cursor noch im Feld steht, und nicht erst, wenn die Bank die
 * Einreichungsdatei zurückweist.
 */

export const ibanNormalisieren = (wert: string | null | undefined): string =>
  String(wert ?? "").replace(/\s+/g, "").toUpperCase();

/** ISO 7064 MOD 97-10, wie in ISO 13616 für die IBAN vorgeschrieben. */
const mod97 = (zeichen: string): number => {
  let rest = 0;
  for (const z of zeichen) {
    const wert = /[0-9]/.test(z) ? z : String(z.charCodeAt(0) - 55);
    for (const ziffer of wert) rest = (rest * 10 + Number(ziffer)) % 97;
  }
  return rest;
};

export const ibanGueltig = (wert: string | null | undefined): boolean => {
  const iban = ibanNormalisieren(wert);
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{10,30}$/.test(iban)) return false;
  // Die ersten vier Zeichen wandern ans Ende, dann Buchstaben → Zahlen.
  return mod97(iban.slice(4) + iban.slice(0, 4)) === 1;
};

/** „DE12 3456 7890 1234 5678 90" — nur für die Anzeige. */
export const ibanFormatieren = (wert: string | null | undefined): string =>
  ibanNormalisieren(wert).replace(/(.{4})/g, "$1 ").trim();

/**
 * Die deutsche Gläubiger-Identifikationsnummer.
 *
 * Aufbau: Länderkennzeichen, zwei Prüfziffern, drei Stellen Geschäftsbereich
 * (meist `ZZZ`) und die Identifikationsnummer. Die Prüfziffern rechnen sich
 * wie bei der IBAN — **ohne** den Geschäftsbereich, der bleibt außen vor.
 */
export const glaeubigerIdGueltig = (wert: string | null | undefined): boolean => {
  const id = ibanNormalisieren(wert);
  if (!/^[A-Z]{2}[0-9]{2}[A-Z0-9]{3}[A-Z0-9]+$/.test(id) || id.length > 35) return false;
  const ohneBereich = id.slice(0, 4) + id.slice(7);
  return mod97(ohneBereich.slice(4) + ohneBereich.slice(0, 4)) === 1;
};
